import React, { useEffect, useState } from 'react'
import { Table } from 'reactstrap'
import styles from 'components/ui/sortableTable.module.scss'
import editIcon from 'assets/img/edit-icon.svg'
import deleteIcon from 'assets/img/delete-icon.svg'
import arrowIcon from 'assets/img/arrow-icon.svg'
import Skeleton from 'react-loading-skeleton'
import { SortFilter } from 'Types/SortFilter'
import Pagination from 'Types/Pagination'
import Paginate from './Paginate'
import 'components/ui/pagination.css'

interface IProps {
  headers: any[]
  data: any[]
  paginateData?: Pagination
  loading?: boolean
  setDeleteModal: (open: boolean) => void
  setDeleteData: (data: any) => void
  setUpdateData: (update: boolean) => void
  setUpdateModal: (open: boolean) => void
  getDataForRow: (id: number) => void
  setSortFilters: (filter: SortFilter[]) => void
  getAllData: () => void
}

const SortableTable: React.FC<IProps> = (props: IProps) => {
  const [sortFilters, setSortFilters] = useState<SortFilter[]>([])

  useEffect(() => {
    props.setSortFilters(sortFilters)
  }, [sortFilters])

  // Sort by one column at a time: clicking a column toggles asc/desc, clicking another replaces it
  function getFilteredData(key: any) {
    const current = sortFilters.find((filter) => filter.key === key)
    setSortFilters([new SortFilter(key, current?.value === 'asc' ? 'desc' : 'asc')])
  }

  function rotatedUp(key: any) {
    const index = sortFilters.findIndex((obj) => obj.key === key)
    if (index !== -1) {
      const oldFilter = sortFilters.find((_, i) => i === index)
      if (oldFilter?.value === 'asc') {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function getSortValue(key: any) {
    return sortFilters.find((filter) => filter.key === key)?.value
  }

  return (
    <>
      <Table className={styles.table} responsive>
        <thead>
          <tr>
            {props.headers.map((header, i) => (
              <th key={i}>
                <button
                  type="button"
                  className={`${styles.sortHeader} ${
                    getSortValue(header.key) ? styles.sortActive : ''
                  }`}
                  onClick={() => getFilteredData(header.key)}
                >
                  {header.value}
                  <img
                    className={rotatedUp(header.key) ? styles.iconRotate : undefined}
                    src={arrowIcon}
                    alt=""
                  />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.data.length === 0 ? (
            props.loading ? (
              [0, 1, 2].map((row) => (
                <tr key={row}>
                  {props.headers.map((_, i) => (
                    <td key={i}>
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td className={styles.noDataText} colSpan={props.headers.length}>
                  Nothing here yet.
                </td>
              </tr>
            )
          ) : (
            props.data.map((data, rowIndex) => (
              <tr key={data.id ?? rowIndex}>
                {props.headers.map((header, i) =>
                  props.headers.length - 1 === i ? (
                    <td key={i}>
                      <div className={styles.lastCell}>
                        <span>{data[header.key]}</span>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.actionButton}
                            aria-label="Edit"
                            title="Edit"
                            onClick={() => {
                              props.setUpdateData(true)
                              props.setUpdateModal(true)
                              props.getDataForRow(data.id)
                            }}
                          >
                            <img src={editIcon} alt="" />
                          </button>
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            aria-label="Delete"
                            title="Delete"
                            onClick={() => {
                              props.setDeleteModal(true)
                              props.setDeleteData(data)
                            }}
                          >
                            <img src={deleteIcon} alt="" />
                          </button>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td key={i}>{data[header.key]}</td>
                  )
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <Paginate
        totalPages={props.paginateData?.totalPages!}
        pageable={props.paginateData?.pageable!}
        paginateData={props.getAllData}
      />
    </>
  )
}

export default SortableTable
